import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import SearchIcon from "../../../../../assets/images/icons/search.svg";
import { showToast } from "../../../../../helper/ShowToast";

import {
    useArtistTrackList,
    useSearchArtistTracks,
    useAlbumPlaylistTracks,
} from "../../../../../queries/artistTracks.queries";

import { getStremingStore } from "../../../../../redux/actions/artistManager";

const tabs = [
    { name: "Single", value: "track" },
    { name: "Project", value: "album" },
    { name: "Playlist", value: "playlist" },
];

/* ---------- debounce ---------- */
const useDebouncedValue = (value, delay = 500) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
};

export default function TrackSelectionModal({
    isOpen,
    onClose,
    artistInfo = {},
    onSelect,
    chosenRelease,
    chosenAutoplayTrack,
}) {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const detailRef = useRef(null);

    const [artistTrackType, setArtistTrackType] = useState("track");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRelease, setSelectedRelease] = useState({});
    const [selectedAlbumPlaylistTrack, setSelectedAlbumPlaylistTrack] =
        useState({});
    const [previousTrackId, setPreviousTrackId] = useState(null);

    /* ---------- sync edit ---------- */
    useEffect(() => {
        if (!isOpen) return;

        if (chosenRelease?.id) {
            setSelectedRelease(chosenRelease);
            setArtistTrackType(chosenRelease.type || "track");
            setPreviousTrackId(chosenRelease.id);
        } else {
            setSelectedRelease({});
            setArtistTrackType("track");
        }

        if (chosenAutoplayTrack?.id) {
            setSelectedAlbumPlaylistTrack(chosenAutoplayTrack);
        } else {
            setSelectedAlbumPlaylistTrack({});
        }
    }, [isOpen, chosenRelease, chosenAutoplayTrack]);

    /* ---------- queries ---------- */
    const debouncedSearch = useDebouncedValue(searchQuery, 500);

    const {
        data: trackList = [],
        isLoading: isTrackListLoading,
    } = useArtistTrackList(artistTrackType, isOpen && !debouncedSearch);

    const {
        data: searchResult,
        isLoading: isSearchLoading,
    } = useSearchArtistTracks(
        {
            artistTrackType,
            query: debouncedSearch,
            artistId: artistInfo?.spotifyArtistId,
            artistName: artistInfo?.spotifyArtistName,
        },
        isOpen && !!debouncedSearch
    );

    const artistTrackList = debouncedSearch
        ? searchResult?.list || []
        : trackList;

    useEffect(() => {
        if (searchResult?.type) {
            setArtistTrackType(searchResult.type);
        }
    }, [searchResult?.type]);

    const {
        data: albumPlaylistTracks,
        isLoading: isLoadingAlbumPlaylistTracks,
    } = useAlbumPlaylistTracks(
        {
            id: selectedRelease?.id,
            type: selectedRelease?.type,
        },
        selectedRelease?.type === "album" ||
        selectedRelease?.type === "playlist"
    );

    /* ---------- handlers ---------- */
    const handleRefresh = () => {
        setSearchQuery("");
        setArtistTrackType("track");
        queryClient.invalidateQueries(["artistTracks"]);
    };

    const handleSelectRelease = (item) => {
        if (!item?.id) return;

        setSelectedRelease(item);
        setSelectedAlbumPlaylistTrack({});

        setTimeout(() => {
            detailRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 120);
    };

    const handleConfirmSelection = () => {
        if (
            (selectedRelease?.type === "album" ||
                selectedRelease?.type === "playlist") &&
            !selectedAlbumPlaylistTrack?.id
        ) {
            showToast("warning", "Please select an autoplay track.");
            return;
        }

        let payload = {};
        let newTrackId;

        if (
            selectedRelease?.type === "album" ||
            selectedRelease?.type === "playlist"
        ) {
            newTrackId = selectedAlbumPlaylistTrack.id;
            payload.autoplayTrackId = selectedAlbumPlaylistTrack.id;
            if (selectedRelease.type === "album")
                payload.albumId = selectedRelease.id;
            if (selectedRelease.type === "playlist")
                payload.playlistId = selectedRelease.id;
        } else {
            newTrackId = selectedRelease.id;
            payload.trackId = selectedRelease.id;
        }

        // if (newTrackId && newTrackId !== previousTrackId) {
        //     dispatch(getStremingStore(payload));
        //     setPreviousTrackId(newTrackId);
        // }
        if (newTrackId) {
            dispatch(getStremingStore(payload));
        }

        onSelect?.(selectedRelease, selectedAlbumPlaylistTrack);
        onClose?.();
    };

    if (!isOpen) return null;

    const safeTrackList = Array.isArray(artistTrackList)
        ? artistTrackList.filter((i) => i?.id)
        : [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/60" onClick={onClose} />

            <div className="relative w-full max-w-[900px] max-h-[90vh] overflow-auto bg-theme_clr1 rounded-lg p-6 font-inter text-white z-50">

                {/* HEADER */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <div>
                        <h3 className="text-[20px] font-semibold">Choose your Release</h3>
                        <div className="text-sm text-white/80">
                            Choose the song, project or playlist you want to promote.
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            className="px-3 py-1 rounded-md bg-white text-black"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-3 py-1 rounded-md bg-theme_clr2 text-white"
                            onClick={handleConfirmSelection}
                            disabled={!selectedRelease?.id}
                        >
                            Use this Release
                        </button>
                    </div>
                </div>

                {/* MAIN LIST */}
                <div className="info-box bg-white rounded-xl text-black">
                    <div className="wrapper border-b p-4 lg:p-5">
                        <div className="text-[18px] font-bold mb-4">
                            Select the release you want to promote.
                        </div>

                        <div className="flex justify-between">
                            <div>Recent Release</div>
                            <div className="text-theme_clr2 cursor-pointer" onClick={handleRefresh}>
                                <i className="bi bi-arrow-clockwise" /> Refresh
                            </div>
                        </div>
                    </div>

                    <div className="p-4 lg:p-5">
                        {/* SEARCH */}
                        <div className="relative mb-4">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2">
                                <img src={SearchIcon} alt="" className="opacity-60" />
                            </span>
                            <input
                                type="search"
                                className="h-[45px] w-full border rounded-md pl-12 pr-4"
                                placeholder="Search for your song, album, or playlist"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {/* TABS */}
                        <div className="flex border-b mb-4">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.value}
                                    onClick={() => setArtistTrackType(tab.value)}
                                    className={`px-4 py-2 font-semibold border-b-2 ${artistTrackType === tab.value
                                        ? "border-theme_clr2 text-theme_clr2"
                                        : "border-transparent text-gray-500"
                                        }`}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </div>

                        {/* LOADER */}
                        {(isTrackListLoading || isSearchLoading) && (
                            <div className="flex justify-center h-32 items-center">
                                <div className="flex space-x-2">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="w-2 h-6 bg-theme_clr2 animate-pulse" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* TRACKS */}
                        {!isTrackListLoading &&
                            !isSearchLoading &&
                            safeTrackList.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() =>
                                        handleSelectRelease({ ...item, type: artistTrackType })
                                    }
                                    className={`flex items-center border rounded p-4 mb-2 cursor-pointer ${selectedRelease?.id === item.id
                                        ? "border-blue-500 ring-2 ring-blue-400"
                                        : ""
                                        }`}
                                >
                                    <img
                                        src={
                                            item?.images?.[0]?.url ||
                                            item?.album?.images?.[0]?.url
                                        }
                                        className="w-12 h-12 rounded-xl object-cover mr-3"
                                        alt=""
                                    />
                                    <div>
                                        <div className="font-medium">{item.name}</div>
                                        {artistTrackType !== "track" && (
                                            <div className="text-sm text-gray-500">
                                                Tracks:{" "}
                                                {artistTrackType === "album"
                                                    ? item.total_tracks
                                                    : item?.tracks?.total}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                {/* SELECTED RELEASE INFO (RESTORED) */}
                {selectedRelease?.id && (
                    <section ref={detailRef} className="mt-6">
                        <div className="heading text-white font-medium text-center mb-3">
                            <h5 className="text-[18px] mb-1">Selected Release</h5>
                            {(selectedRelease.type === 'playlist' ||
                                selectedRelease.type === 'album') &&
                                albumPlaylistTracks?.items?.length > 0 && (
                                    <div className="text-white text-opacity-80 text-[13px]">
                                        Choose a song to autoplay when your ad is clicked.
                                    </div>
                                )}
                        </div>

                        <div className="bg-white rounded-xl">
                            <div className="divide-y divide-gray-100">

                                {/* Header */}
                                <div className="flex items-center space-x-4 p-4">
                                    <img
                                        src={
                                            selectedRelease?.images?.[0]?.url ||
                                            selectedRelease?.album?.images?.[0]?.url
                                        }
                                        alt=""
                                        className="w-12 h-12 rounded-xl object-cover"
                                    />

                                    <div className="text-black text-base font-bold">
                                        {selectedRelease?.name}
                                    </div>
                                </div>

                                {/* Creator */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
                                    <div className="text-black font-bold">Creator</div>
                                    <div className="text-gray-700 uppercase">
                                        {(selectedRelease?.artists?.[0]?.name ||
                                            selectedRelease?.owner?.display_name ||
                                            'Unknown').toUpperCase()}
                                    </div>
                                </div>

                                {/* Type */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
                                    <div className="text-black font-bold">Type</div>
                                    <div className="text-gray-700 uppercase">{selectedRelease?.type}</div>
                                </div>

                                {/* Found On */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
                                    <div className="text-black font-bold">Found On</div>
                                    <div className="text-gray-700 uppercase">
                                        Spotify <i className="bi bi-spotify"></i>
                                    </div>
                                </div>

                                {/* Autoplay track dropdown */}
                                {(selectedRelease.type === 'playlist' ||
                                    selectedRelease.type === 'album') && (
                                        <div className="p-4">
                                            <div className="text-base text-black font-bold mb-1">
                                                Autoplay a Track
                                            </div>
                                            <div className="text-gray-500 text-sm mb-3">
                                                Select a song to autoplay when someone clicks your ad.
                                            </div>

                                            {/*MUSIC LOADER */}
                                            {isLoadingAlbumPlaylistTracks && (
                                                <div className="flex justify-center items-center h-32 space-y-2 animate-pulse">
                                                    <div className="flex space-x-2">
                                                        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" />
                                                        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" />
                                                        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" />
                                                        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" />
                                                    </div>
                                                </div>
                                            )}

                                            {/* AUTOPLAY DROPDOWN */}
                                            {!isLoadingAlbumPlaylistTracks && albumPlaylistTracks?.items?.length > 0 && (
                                                <select
                                                    className="h-[45px] w-full bg-white border border-slate-300 rounded-md px-4 py-2 text-black"
                                                    value={selectedAlbumPlaylistTrack?.id || '0'}
                                                    onChange={(e) => {
                                                        const id = e.target.value;
                                                        const selectedObj =
                                                            albumPlaylistTracks?.items?.find(
                                                                (item) => String(item.id) === String(id)
                                                            );
                                                        setSelectedAlbumPlaylistTrack(selectedObj || {});
                                                    }}
                                                >
                                                    <option value="0">Select a song</option>

                                                    {albumPlaylistTracks.items.map((item) => (
                                                        <option key={item.id} value={item.id}>
                                                            {item.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            {/*NO TRACKS FALLBACK */}
                                            {!isLoadingAlbumPlaylistTracks && albumPlaylistTracks?.items?.length === 0 && (
                                                <div className="text-sm text-gray-500">
                                                    No tracks available for autoplay.
                                                </div>
                                            )}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </section>
                )}

                {/* BOTTOM ACTION BUTTONS (RESTORED) */}
                <div className="flex justify-end gap-2 mt-5">
                    <button className="px-3 py-1 rounded bg-white text-black" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="px-3 py-1 rounded bg-theme_clr2 text-white"
                        onClick={handleConfirmSelection}
                        disabled={!selectedRelease?.id}
                    >
                        Use this Release
                    </button>
                </div>
            </div>
        </div>
    );
}

TrackSelectionModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    artistInfo: PropTypes.object,
    onSelect: PropTypes.func,
    chosenRelease: PropTypes.object,
    chosenAutoplayTrack: PropTypes.object,
};
